import type { UIMessage } from '@ai-sdk/react'
import { getToolName, isToolUIPart } from 'ai'

export type Part = UIMessage['parts'][number]

export const getTextContent = (message: UIMessage): string =>
  message.parts
    .filter((part): part is Extract<Part, { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('')

export const isToolPart = (part: Part): boolean => isToolUIPart(part)

interface ToolLabel {
  doing: string
  /**
   * Wording once the tool returns, or `null` to say nothing at all. Use `null`
   * when Buddy's own reply is the confirmation — a system label that announces
   * an outcome can contradict the sentence printed right after it.
   */
  done: string | null
}

// This is an ALLOWLIST, not a set of overrides. A tool with no entry renders
// nothing: no label while it runs, no tick when it finishes. It used to fall back
// to a humanized version of the raw tool name, which showed customers the names of
// our internals — "Dobby...", "Pai voice...", "Tdlc status lookup...",
// "Get enterprise context...", "Estimate enterprise spend...". Adding a backend tool
// must not be able to leak its name into the chat, so new tools are silent until
// someone writes human wording for them here.
// Ref: India Enterprise flow spec, task 8 "stop showing customers our internal tools".
const TOOL_LABELS: Record<string, ToolLabel> = {
  // aiassist BM25 doc tools — both surface the same user-facing intent.
  grep_docs: { doing: 'Searching docs', done: 'Searched docs' },
  read_doc: { doing: 'Searching docs', done: 'Searched docs' },
  // Support flows
  escalate_to_support: {
    doing: 'Forwarding request to support',
    done: 'Forwarded support request',
  },
  grant_ai_agent_access: { doing: 'Updating permission', done: 'Updated permission' },
  // `done: null` on both request flows. The tool can come back declined by the
  // product gate (India SMS, reseller, no commitment), and "Submitted enterprise
  // request" printed above Buddy explaining it cannot sell them that is the exact
  // success-then-refusal the spec calls out. Buddy's own sentence is the outcome.
  request_enterprise: { doing: 'Passing this to our team', done: null },
  request_outbound_calling_access: { doing: 'Passing this to our team', done: null },
  // Lookups — plain human wording, never the tool's name.
  pricing_lookup: { doing: 'Looking up pricing', done: 'Looked up pricing' },
  get_enterprise_context: { doing: 'Checking your account', done: null },
  account_limits_lookup: { doing: 'Checking your account limits', done: 'Checked your limits' },
  get_outbound_calling_status: { doing: 'Checking your account', done: null },
  compliance_status_lookup: {
    doing: 'Checking your compliance status',
    done: 'Checked your compliance status',
  },
  tdlc_status_lookup: {
    doing: 'Checking your registration status',
    done: 'Checked your registration status',
  },
  port_in_status_lookup: { doing: 'Checking your port-in', done: 'Checked your port-in' },
  // Flow-edition split actions (one tool became several HTTP actions).
  port_in_order_search: { doing: 'Checking your port-in', done: 'Checked your port-in' },
  port_in_order_detail: { doing: 'Checking your port-in', done: 'Checked your port-in' },
  compliance_application_lookup: {
    doing: 'Checking your compliance status',
    done: 'Checked your compliance status',
  },
  tdlc_submissions_lookup: {
    doing: 'Checking your registration status',
    done: 'Checked your registration status',
  },
  tdlc_brands_lookup: {
    doing: 'Checking your registration status',
    done: 'Checked your registration status',
  },
  tdlc_campaigns_lookup: {
    doing: 'Checking your registration status',
    done: 'Checked your registration status',
  },
  tdlc_campaign_numbers_lookup: {
    doing: 'Checking your registration status',
    done: 'Checked your registration status',
  },
  zentrunk_pricing_lookup: { doing: 'Looking up pricing', done: 'Looked up pricing' },
  csr_lookup: { doing: 'Looking that up', done: 'Looked that up' },
  list_recent_tickets: { doing: 'Checking your recent tickets', done: 'Checked your tickets' },
  append_to_ticket: { doing: 'Adding this to your ticket', done: 'Added this to your ticket' },
  // Debugger handoff + sub-agent tools. Reachable through the pai-voice block
  // mapper; the direct route_to_* / debugger calls are suppressed server-side.
  route_to_debugger: { doing: 'Switching to debug mode', done: 'Switched to debug mode' },
  call_voice_sip_debugger: { doing: 'Investigating voice call', done: 'Investigated voice call' },
  call_messaging_debugger: { doing: 'Investigating message', done: 'Investigated message' },
  // Deliberately absent, and must stay absent: `dobby`, `pai_voice` and
  // `estimate_enterprise_spend`. The first two are internal codenames; the third
  // announces a spend calculation the customer never asked for and should not see.
}

/** Human wording for a tool, or `null` when it must render nothing. */
export const getToolLabel = (toolName: string, isDone: boolean): string | null => {
  const mapped = TOOL_LABELS[toolName]
  if (!mapped) return null
  return isDone ? mapped.done : `${mapped.doing}...`
}

export const MAX_LENGTH = 1000

const BUDDY_SLASH_COMMANDS = ['ticket'] as const

// Completes a partial slash command (e.g. "/tick" -> "/ticket") when Tab is pressed.
export const completeSlashCommand = (value: string): string | null => {
  const leadingWhitespace = value.match(/^\s*/)?.[0] ?? ''
  const trimmed = value.slice(leadingWhitespace.length)
  if (!trimmed.startsWith('/')) return null

  const afterSlash = trimmed.slice(1)
  const spaceIdx = afterSlash.search(/\s/)
  const commandPart = (spaceIdx === -1 ? afterSlash : afterSlash.slice(0, spaceIdx)).toLowerCase()
  const suffix = spaceIdx === -1 ? '' : afterSlash.slice(spaceIdx)

  const matches = BUDDY_SLASH_COMMANDS.filter((cmd) => cmd.startsWith(commandPart))
  if (matches.length !== 1) return null

  const completedCommand = matches[0]
  if (commandPart === completedCommand) return null

  return `${leadingWhitespace}/${completedCommand}${suffix}`
}

export interface BuddySuggestion {
  text: string
  testId: string
}

const SMS_SUGGESTION: BuddySuggestion = {
  text: 'How do I send an SMS with Plivo?',
  testId: 'assistant-suggestion-sms',
}
const CALL_SUGGESTION: BuddySuggestion = {
  text: 'How do I make an outbound call?',
  testId: 'assistant-suggestion-call',
}
const NUMBER_SUGGESTION: BuddySuggestion = {
  text: 'How do I configure a phone number?',
  testId: 'assistant-suggestion-number',
}
const VOICE_AGENT_SUGGESTION: BuddySuggestion = {
  text: 'How do I build a voice agent?',
  testId: 'assistant-suggestion-voice-agent',
}

// India drops the SMS chip (SMS isn't a primary India use case) and the support-team
// chip (3 total); other regions keep SMS, drop support-team, and add the voice-agent
// chip (4 total). testIds are stable regardless of copy changes.
export const getBuddySuggestions = (isIndiaRegion: boolean): BuddySuggestion[] =>
  isIndiaRegion
    ? [CALL_SUGGESTION, NUMBER_SUGGESTION, VOICE_AGENT_SUGGESTION]
    : [SMS_SUGGESTION, CALL_SUGGESTION, NUMBER_SUGGESTION, VOICE_AGENT_SUGGESTION]

// Strip control chars except tab, newline, CR. Lookahead skips \t \n \r so
// multi-line input typed with Enter survives sanitisation.
export const sanitizeInput = (text: string): string =>
  text.replace(/(?![\t\n\r])\p{Cc}/gu, '').trim()

export const is429Error = (error: Error) =>
  error.message?.includes('429') || error.message?.includes('Rate limit')

// Aiassist Buddy emits `escalate_to_support` (was `send_support_email` on sentinel).
// Treat the banner as resolved once that tool has fired in any prior turn — the
// success/failure distinction is conveyed via the inline tool-output UI.
export const hasCompletedEscalation = (messages: UIMessage[]): boolean =>
  messages.some(
    (msg) =>
      msg.role === 'assistant' &&
      msg.parts.some((part) => isToolUIPart(part) && getToolName(part) === 'escalate_to_support'),
  )
